import { useHistory, withRouter } from "react-router-dom/cjs/react-router-dom.min";
import { LayoutSingleColumn, NamedLink, Page } from "../../components";
import FooterComponent from "../FooterContainer/FooterContainer";
import TopbarContainer from "../TopbarContainer/TopbarContainer";
import css from "./FractionalBlogPage.module.css"
import { manageDisableScrolling, isScrollingDisabled } from "../../ducks/ui.duck";
import { connect } from "react-redux";
import { compose } from "redux";
import { injectIntl } from "react-intl";
import FractionalBlogPost from "./FractionalBlogPosts";
import VlogSection from "./VlogSection";
import FortehBlogSection from "./FortehBlogSection";
import { useEffect, useRef, useState } from "react";
import { getBlogsAdmin, getVlogs } from "../../util/api";
import { createResourceLocatorString } from "../../util/routes";
import { useRouteConfiguration } from "../../context/routeConfigurationContext";
import { useConfiguration } from "../../context/configurationContext";

export const FractionalBlogPageComponent = props => {
    const history = useHistory()
    const routeConfiguration = useRouteConfiguration()
    const config = useConfiguration()
    const [activeTab, setActiveTab] = useState('published');

    const {
        scrollingDisabled,
        onManageDisableScrolling,
        currentUser,
        blogsData,
        vlogsData,
        getBlogsInProgress,
        getVlogsInProgress
    } = props;

    // Use Redux data if available, otherwise set locally. We use local state mainly for Admin fetching (drafts)
    const [blogs, setBlogs] = useState(blogsData || []);
    const [vlogs, setVlogs] = useState(Array.isArray(vlogsData) ? vlogsData : vlogsData?.data || []);
    const [loading, setLoading] = useState(getBlogsInProgress);
    const [vlogsLoading, setVlogsLoading] = useState(getVlogsInProgress);
    const vlogsFetchedRef = useRef(false);

    const isAdmin = (currentUser && currentUser?.id && currentUser?.attributes?.profile?.privateData?.admin);

    const fetchVlogs = async () => {
        setVlogsLoading(true);
        try {
            const vlogsRes = await getVlogs();
            setVlogs(vlogsRes?.data || []);
        } catch (err) {
            console.error("Failed fetching vlogs", err);
        } finally {
            setVlogsLoading(false);
        }
    };

    const fetchBlogs = async () => {
        if (isAdmin) {
            setLoading(true);
            try {
                const blogsRes = await getBlogsAdmin();
                setBlogs(blogsRes);
            } catch (err) {
                console.error("Failed fetching admin data", err);
            } finally {
                setLoading(false);
            }
        } else if (!blogsData || blogsData.length === 0) {
             // Fallback if accessed via client side navigation without Redux fetch
             setBlogs(blogsData || []);
        } else {
             setBlogs(blogsData || []);
        }
    };

    useEffect(() => {
        // Redux SSR loads the user data. Admins might need to load drafts separately.
        const fetchData = async () => {
            await fetchBlogs();
            // Only fetch vlogs once on initial load if Redux data is empty
            if (!vlogsFetchedRef.current && (!vlogsData || vlogsData.length === 0)) {
                vlogsFetchedRef.current = true;
                await fetchVlogs();
            }
            setLoading(getBlogsInProgress);
        };

        fetchData();
    }, [isAdmin, blogsData, vlogsData, getBlogsInProgress, getVlogsInProgress]);

    // Sync vlogs from Redux when data changes (without triggering fetch)
    useEffect(() => {
        const vlogsArray = Array.isArray(vlogsData) ? vlogsData : vlogsData?.data || [];
        setVlogs(vlogsArray);
        setVlogsLoading(getVlogsInProgress);
    }, [vlogsData, getVlogsInProgress]);

    const allBlogs = blogs?.data || [];
    const publishedBlogs = allBlogs.filter(
        blog => blog.status === 'published'
    );
    const draftBlogs = allBlogs.filter(
        blog => blog.status === 'draft'
    );
    const visibleBlogs = activeTab === 'published' ? publishedBlogs : draftBlogs;

    const handleBlog = blog => {
        history.push(
            createResourceLocatorString(
                'PostPage',
                routeConfiguration,
                { id: blog._id, slug: blog.slug },
                {}
            )
        );
    };

    // SEO: Create structured data for blog listing
    const createBlogListingSchema = () => {
        const blogPosts = visibleBlogs.map((blog, index) => ({
            '@type': 'BlogPosting',
            headline: blog.title,
            description: blog.description || blog.excerpt,
            image: blog.image,
            datePublished: blog.publishedAt,
            dateModified: blog.updatedAt,
            author: {
                '@type': 'Person',
                name: 'Raman',
                jobTitle: 'Founder of Forteh'
            },
            publisher: {
                '@type': 'Organization',
                name: config.marketplaceName,
                logo: {
                    '@type': 'ImageObject',
                    url: config.branding.logoImageMobileURL
                }
            },
            url: `${config.marketplaceRootURL}/everything-fractional/${blog._id}/${blog.slug}`
        }));

        return {
            '@context': 'https://schema.org',
            '@type': 'Blog',
            name: 'Forteh Fractional Blog',
            description: 'Everything Fractional - Insights, tips, and trends about fractional work and the future of careers.',
            url: `${config.marketplaceRootURL}/everything-fractional`,
            publisher: {
                '@type': 'Organization',
                name: config.marketplaceName,
                logo: {
                    '@type': 'ImageObject',
                    url: config.branding.logoImageMobileURL
                }
            },
            author: {
                '@type': 'Person',
                name: 'Raman',
                jobTitle: 'Founder of Forteh'
            },
            blogPost: blogPosts
        };
    };

    // SEO: Create breadcrumb schema
    const createBreadcrumbSchema = () => {
        return {
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: [
                {
                    '@type': 'ListItem',
                    position: 1,
                    name: 'Home',
                    item: config.marketplaceRootURL
                },
                {
                    '@type': 'ListItem',
                    position: 2,
                    name: 'Blog',
                    item: `${config.marketplaceRootURL}/everything-fractional`
                }
            ]
        };
    };

    const schema = [createBlogListingSchema(), createBreadcrumbSchema()];

    return (
        <Page 
            title="Everything Fractional - Forteh Blog" 
            description="Discover insights, tips, and trends about fractional work and the future of careers. Explore our comprehensive guide to fractional employment and remote work opportunities."
            scrollingDisabled={scrollingDisabled}
            schema={schema}
            openGraphType="website"
            facebookImages={[
                {
                    width: 1200,
                    height: 630,
                    url: config.branding.facebookImage
                }
            ]}
            twitterImages={[
                {
                    width: 600,
                    height: 314,
                    url: config.branding.twitterImage
                }
            ]}
            socialSharing={{
                title: "Everything Fractional - Forteh Blog",
                description: "Discover insights, tips, and trends about fractional work and the future of careers. Explore our comprehensive guide to fractional employment and remote work opportunities.",
                images1200: [
                    {
                        width: 1200,
                        height: 630,
                        url: config.branding.facebookImage
                    }
                ],
                images600: [
                    {
                        width: 600,
                        height: 314,
                        url: config.branding.twitterImage
                    }
                ]
            }}
        >
            <LayoutSingleColumn
                topbar={
                    <TopbarContainer
                        className={css.topbar}
                        mobileRootClassName={css.mobileTopbar}
                        desktopClassName={css.desktopTopbar}
                        currentPage="FractionalBlogPage"
                    />
                }
                footer={<FooterComponent />}
                className={css.pageRoot}
            >
                <div className={css.outerWrapper}>
                    <VlogSection
                        vlogs={vlogs}
                        isAdmin={isAdmin}
                        onManageDisableScrolling={onManageDisableScrolling}
                        onVlogsUpdate={fetchVlogs}
                        vlogsLoading={vlogsLoading}
                    />

                    {/* <div className={css.postWrap}>
                        <h2>RECENT BLOGS</h2>
                        <h6>Here you'll find all our Fractional Blog Posts!</h6>

                        {isAdmin && (
                            <div className={css.tabs}>
                                <button
                                    className={activeTab === 'published' ? css.activeTab : ''}
                                    onClick={() => setActiveTab('published')}
                                >
                                    Published
                                </button>
                                <button
                                    className={activeTab === 'draft' ? css.activeTab : ''}
                                    onClick={() => setActiveTab('draft')}
                                >
                                    Drafts
                                </button>
                            </div>
                        )}

                        <FractionalBlogPost
                            blogs={blogs}
                            handleBlog={handleBlog}
                            loading={loading}
                            setActiveTab={setActiveTab}
                            activeTab={activeTab}
                            isAdmin={isAdmin}
                            visibleBlogs={visibleBlogs}
                            onBlogsUpdate={fetchBlogs}
                            onManageDisableScrolling={onManageDisableScrolling}
                        />
                        {isAdmin && (
                            <div className={css.addVlogBtnWrapper}>
                                <NamedLink className={css.more} name="AdminBlogPage">+ Add Blog</NamedLink>
                            </div>
                        )}
                    </div> */}

                    <FortehBlogSection isAdmin={isAdmin} />
                </div>
            </LayoutSingleColumn>
        </Page>
    );
};



const mapStateToProps = state => {
    const { currentUser } = state.user;
    const { blogs, vlogs, getBlogsInProgress, getVlogsInProgress } = state.FractionalBlogPage || {};
    return {
        currentUser,
        blogsData: blogs,
        vlogsData: vlogs,
        getBlogsInProgress,
        getVlogsInProgress,
        scrollingDisabled: isScrollingDisabled(state),
    };
};

const mapDispatchToProps = dispatch => ({
    onManageDisableScrolling: (componentId, disableScrolling) =>
        dispatch(manageDisableScrolling(componentId, disableScrolling)),
});

const FractionalBlogPage = compose(
    withRouter,
    connect(
        mapStateToProps,
        mapDispatchToProps
    ),
    injectIntl
)(FractionalBlogPageComponent);

export default FractionalBlogPage;